// MenuSearchEntidades.js
import { useEffect, useMemo, useState } from "react";

import api, { handleApi } from "pages/api/api";
import { useAlert } from "pages/context/AlertContext";
import { useAuth } from "pages/context/AuthContext";
import { useTable, useFilters } from "react-table";

import { useTheme } from "@mui/material/styles";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import CircularProgress from "@mui/material/CircularProgress";
import Tooltip from "@mui/material/Tooltip";
import IconButton from "@mui/material/IconButton";
import Icon from "@mui/material/Icon";
import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";

import DefaultLineChart from "examples/Charts/LineCharts/DefaultLineChart";
import MDButton from "components/MDButton";

import DialogClienteContactos from "./DialogClienteContactos";

const DEFAULT_MAX_FATURAS = 20;

const mesesLabels = [
  "Jan",
  "Fev",
  "Mar",
  "Abr",
  "Mai",
  "Jun",
  "Jul",
  "Ago",
  "Set",
  "Out",
  "Nov",
  "Dez",
];

export default function MenuSearchEntidades({ open, onClose, entidade }) {
  const { showAlert } = useAlert();
  const { temFuncao, userData } = useAuth();
  const theme = useTheme();

  const [loading, setLoading] = useState(false); // tabela
  const [linhas, setLinhas] = useState([]);
  const [maxFaturas, setMaxFaturas] = useState(DEFAULT_MAX_FATURAS);

  // preços por material (apenas alguns perfis)
  const [precosOpen, setPrecosOpen] = useState(false);
  const [precosLoading, setPrecosLoading] = useState(false);
  const [precos, setPrecos] = useState(null);
  const [precosRefMat, setPrecosRefMat] = useState("");

  // gráfico
  const [vendasResumo, setVendasResumo] = useState([]);
  const [loadingResumo, setLoadingResumo] = useState(false);

  const [openContactos, setOpenContactos] = useState(false);
  const [resumoContactos, setResumoContactos] = useState(null);
  const [loadingContactosResumo, setLoadingContactosResumo] = useState(false);

  const [propagacaoSubmarcas, setPropagacaoSubmarcas] = useState(null);
  const [loadingPropagacaoSubmarcas, setLoadingPropagacaoSubmarcas] = useState(false);

  const [debitosCliente, setDebitosCliente] = useState([]);
  const [loadingDebitosCliente, setLoadingDebitosCliente] = useState(false);

  const [fabricantesPropagacao, setFabricantesPropagacao] = useState([]);
  const [fabricantePropagacaoSel, setFabricantePropagacaoSel] = useState(null);
  const [loadingFabricantesPropagacao, setLoadingFabricantesPropagacao] = useState(false);

  const [dataIniPropagacaoSel, setDataIniPropagacaoSel] = useState("");
  const [dataFimPropagacaoSel, setDataFimPropagacaoSel] = useState("");

  const [dataIniPropagacaoEdit, setDataIniPropagacaoEdit] = useState("");
  const [dataFimPropagacaoEdit, setDataFimPropagacaoEdit] = useState("");

  const colorMap = {
    warning: theme.palette.warning.main,
    primary: theme.palette.primary.main,
    secondary: theme.palette.secondary.main,
  };

  // Quem pode ver valores monetários
  const podeVerValores =
    temFuncao("Administração") ||
    temFuncao("Informática") ||
    temFuncao("Tesouraria") ||
    temFuncao("Comercial");

  // Quem pode ver o botão VER PREÇOS (igual a podeVerValores mas SEM Comercial)
  const podeVerPrecos =
    temFuncao("Administração") || temFuncao("Informática") || temFuncao("Tesouraria");

  // refvendedor do utilizador corrente (se for Comercial)
  const refVendedorAtual = temFuncao("Comercial") ? (userData?.refvendedor || "").trim() : "";

  const FABRICANTE_STORAGE_KEY = `propagacaoSubmarcas:fabricante:${userData?.id || 0}`;
  const DATAINI_STORAGE_KEY = `propagacaoSubmarcas:dataIni:${userData?.id || 0}`;
  const DATAFIM_STORAGE_KEY = `propagacaoSubmarcas:dataFim:${userData?.id || 0}`;

  // -------------------- Carregar dados --------------------
  useEffect(() => {
    let active = true;

    const carregar = async () => {
      if (!open || !entidade) return;

      const refentidade = (entidade.ref || entidade.refentidade || "").trim();
      if (!refentidade) return;

      // só avançamos se maxFaturas for um inteiro positivo
      const n = parseInt(maxFaturas, 10);
      if (!Number.isFinite(n) || n <= 0) return;

      // reset locais
      setLinhas([]);
      setVendasResumo([]);
      setResumoContactos(null);
      setPropagacaoSubmarcas(null);
      setFabricantesPropagacao([]);
      setFabricantePropagacaoSel(null);
      setDebitosCliente([]);

      setLoading(true);
      setLoadingResumo(true);
      setLoadingContactosResumo(true);
      setLoadingPropagacaoSubmarcas(true);
      setLoadingFabricantesPropagacao(true);
      setLoadingDebitosCliente(true);

      // ---- 1) dados para o gráfico (resumo) ----
      const [dadosResumo, errResumo] = await handleApi(
        api.post("Entidades/SelVendasPorEntidadeResumo", {
          refentidade,
          refvendedor: refVendedorAtual,
        })
      );

      // ---- 1.5) resumo compacto de contactos ----
      const [dadosResumoContactos, errResumoContactos] = await handleApi(
        api.post("Comercial/SelResumoContactos", {
          refcliente: refentidade,
          refvendedor: refVendedorAtual,
        })
      );

      let refFabricantePropagacao = "";
      let dataIniPropagacao = dataIniPropagacaoSel || "";
      let dataFimPropagacao = dataFimPropagacaoSel || "";

      try {
        refFabricantePropagacao = localStorage.getItem(FABRICANTE_STORAGE_KEY) || "";

        if (!dataIniPropagacao) {
          dataIniPropagacao = localStorage.getItem(DATAINI_STORAGE_KEY) || "";
        }

        if (!dataFimPropagacao) {
          dataFimPropagacao = localStorage.getItem(DATAFIM_STORAGE_KEY) || "";
        }
      } catch {
        refFabricantePropagacao = "";
      }

      if (!dataIniPropagacao || !dataFimPropagacao) {
        const [dadosPeriodoDefault, errPeriodoDefault] = await handleApi(
          api.post("Comercial/SelPeriodoDefaultPropagacaoSubmarcas", {})
        );

        if (errPeriodoDefault) {
          showAlert(errPeriodoDefault, "error");
        } else {
          const periodoDefault =
            dadosPeriodoDefault && typeof dadosPeriodoDefault === "object"
              ? dadosPeriodoDefault
              : dadosPeriodoDefault?.data && typeof dadosPeriodoDefault.data === "object"
              ? dadosPeriodoDefault.data
              : null;

          dataIniPropagacao = periodoDefault?.dataini || "";
          dataFimPropagacao = periodoDefault?.datafim || "";
        }
      }

      if (!dataIniPropagacaoSel && dataIniPropagacao) {
        setDataIniPropagacaoSel(dataIniPropagacao);
      }

      if (!dataFimPropagacaoSel && dataFimPropagacao) {
        setDataFimPropagacaoSel(dataFimPropagacao);
      }

      if (!dataIniPropagacaoEdit && dataIniPropagacao) {
        setDataIniPropagacaoEdit(dataIniPropagacao);
      }

      if (!dataFimPropagacaoEdit && dataFimPropagacao) {
        setDataFimPropagacaoEdit(dataFimPropagacao);
      }

      const [dadosFabricantesPropagacao, errFabricantesPropagacao] = await handleApi(
        api.post("Comercial/SelFabricantesClientePropagacao", {
          refcliente: refentidade,
          refvendedor: refVendedorAtual,
          dataini: dataIniPropagacao,
          datafim: dataFimPropagacao,
        })
      );

      let fabricantesPropagacaoLista = [];

      if (errFabricantesPropagacao) {
        showAlert(errFabricantesPropagacao, "error");
      } else {
        fabricantesPropagacaoLista = Array.isArray(dadosFabricantesPropagacao)
          ? dadosFabricantesPropagacao
          : Array.isArray(dadosFabricantesPropagacao?.data)
          ? dadosFabricantesPropagacao.data
          : [];
      }

      if (!refFabricantePropagacao) {
        const [dadosFabricanteTop, errFabricanteTop] = await handleApi(
          api.post("Comercial/SelFabricanteTopCliente", {
            refcliente: refentidade,
            refvendedor: refVendedorAtual,
            dataini: dataIniPropagacao,
            datafim: dataFimPropagacao,
          })
        );

        if (errFabricanteTop) {
          showAlert(errFabricanteTop, "error");
        } else {
          const fabricanteTop =
            dadosFabricanteTop && typeof dadosFabricanteTop === "object"
              ? dadosFabricanteTop
              : dadosFabricanteTop?.data && typeof dadosFabricanteTop.data === "object"
              ? dadosFabricanteTop.data
              : null;

          refFabricantePropagacao = (fabricanteTop?.reffabricante || "").trim();
        }
      }

      let fabricanteSelAtual = null;

      if (fabricantesPropagacaoLista.length > 0) {
        fabricanteSelAtual =
          fabricantesPropagacaoLista.find(
            (x) => (x?.ref || "").toString().trim() === refFabricantePropagacao
          ) || fabricantesPropagacaoLista[0];

        refFabricantePropagacao = (fabricanteSelAtual?.ref || "").toString().trim();
      }

      const [dadosPropagacaoSubmarcas, errPropagacaoSubmarcas] = refFabricantePropagacao
        ? await handleApi(
            api.post("Comercial/SelPropagacaoSubmarcasCliente", {
              refcliente: refentidade,
              refvendedor: refVendedorAtual,
              reffabricante: refFabricantePropagacao,
              dataini: dataIniPropagacao,
              datafim: dataFimPropagacao,
            })
          )
        : [null, null];

      const [dadosDebitosCliente, errDebitosCliente] = await handleApi(
        api.post("Comercial/SelClienteDebitosDialog", {
          refcliente: refentidade,
          refvendedor: refVendedorAtual,
        })
      );

      if (!active) return;

      setLoadingContactosResumo(false);
      setLoadingPropagacaoSubmarcas(false);
      setLoadingFabricantesPropagacao(false);
      setLoadingDebitosCliente(false);

      setFabricantesPropagacao(fabricantesPropagacaoLista);
      setFabricantePropagacaoSel(fabricanteSelAtual);

      if (errResumoContactos) {
        showAlert(errResumoContactos, "error");
        setResumoContactos(null);
      } else {
        const row =
          dadosResumoContactos && typeof dadosResumoContactos === "object"
            ? dadosResumoContactos
            : dadosResumoContactos?.data && typeof dadosResumoContactos.data === "object"
            ? dadosResumoContactos.data
            : null;

        setResumoContactos(row);
      }

      if (errPropagacaoSubmarcas) {
        showAlert(errPropagacaoSubmarcas, "error");
        setPropagacaoSubmarcas(null);
      } else {
        const row =
          dadosPropagacaoSubmarcas && typeof dadosPropagacaoSubmarcas === "object"
            ? dadosPropagacaoSubmarcas
            : dadosPropagacaoSubmarcas?.data && typeof dadosPropagacaoSubmarcas.data === "object"
            ? dadosPropagacaoSubmarcas.data
            : null;

        setPropagacaoSubmarcas(row);
      }

      if (errDebitosCliente) {
        showAlert(errDebitosCliente, "error");
        setDebitosCliente([]);
      } else {
        const rows = Array.isArray(dadosDebitosCliente)
          ? dadosDebitosCliente
          : Array.isArray(dadosDebitosCliente?.data)
          ? dadosDebitosCliente.data
          : [];

        setDebitosCliente(rows);
      }

      if (!active) return;

      setLoadingResumo(false);

      if (errResumo) {
        showAlert(errResumo, "error");
        setVendasResumo([]);
      } else {
        setVendasResumo(Array.isArray(dadosResumo) ? dadosResumo : []);
      }

      // ---- 2) linhas de faturas (tabela) ----
      const [dados, err] = await handleApi(
        api.post("Entidades/SelVendasPorEntidade", {
          refentidade,
          refvendedor: refVendedorAtual,
          maxfaturas: n,
        })
      );

      if (!active) return;

      setLoading(false);

      if (err) {
        showAlert(err, "error");
        setLinhas([]);
        return;
      }

      setLinhas(Array.isArray(dados) ? dados : []);
    };

    if (open && entidade) {
      carregar();
    } else if (!open) {
      setLinhas([]);
      setVendasResumo([]);
      setResumoContactos(null);
      setPropagacaoSubmarcas(null);

      setFabricantesPropagacao([]);
      setFabricantePropagacaoSel(null);
      setDataIniPropagacaoSel("");
      setDataFimPropagacaoSel("");
      setDataIniPropagacaoEdit("");
      setDataFimPropagacaoEdit("");
      setDebitosCliente([]);

      setLoading(false);
      setLoadingResumo(false);
      setLoadingContactosResumo(false);
      setLoadingPropagacaoSubmarcas(false);
      setLoadingFabricantesPropagacao(false);
      setLoadingDebitosCliente(false);
      setOpenContactos(false);
    }

    return () => {
      active = false;
    };
  }, [
    open,
    entidade,
    maxFaturas,
    refVendedorAtual,
    showAlert,
    temFuncao,
    dataIniPropagacaoSel,
    dataFimPropagacaoSel,
  ]);

  // -------------------- Helpers --------------------

  const titulo = entidade
    ? `Faturas de ${entidade.nome || ""}${entidade.nif ? ` (NIF: ${entidade.nif})` : ""} ( Refª: ${
        entidade.ref ?? ""
      })`
    : "Faturas da entidade";

  const formatData = (v) => {
    if (!v) return "";
    const s = String(v);
    return s.substring(0, 10);
  };

  const formatDataPt = (v) => {
    if (!v) return "";
    const dt = new Date(v);
    if (Number.isNaN(dt.getTime())) return String(v);
    return dt.toLocaleDateString("pt-PT");
  };

  const formatDateTime = (v) => {
    if (!v) return "";
    const dt = new Date(v);
    if (Number.isNaN(dt.getTime())) return "";
    return dt.toLocaleString("pt-PT", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const formatNum = (v, dec = 2) => {
    if (v === null || v === undefined || v === "") return "";
    const n = Number(v);
    if (!Number.isFinite(n)) return "";
    return n.toLocaleString("pt-PT", {
      minimumFractionDigits: dec,
      maximumFractionDigits: dec,
    });
  };

  const joinValPerc = (val, perc, withEuro = false) => {
    const v = formatNum(val);
    const p = formatNum(perc, 1);
    let s = "";
    if (v) s += `${v}${withEuro ? " €" : ""}`;
    if (p) s += `${s ? " " : ""}(${p}%)`;
    return s;
  };

  const calcularMargem = (precosObj) => {
    if (!precosObj) {
      return { margemValor: null, margemPerc: null, margemTexto: "" };
    }

    const venda = Number(precosObj.zpr0 ?? 0);
    const custo = Number(precosObj.zpvr ?? 0);

    if (!Number.isFinite(venda) || !Number.isFinite(custo) || custo === 0) {
      return { margemValor: null, margemPerc: null, margemTexto: "" };
    }

    const margemValor = venda - custo;
    // margem % calculada sobre o custo (mais intuitivo: 100% = vender ao dobro do custo)
    const margemPerc = (margemValor / custo) * 100;

    return {
      margemValor,
      margemPerc,
      margemTexto: joinValPerc(margemValor, margemPerc, true), // "X,XX € (Y,Y%)"
    };
  };

  const abrirPrecosMaterial = async (refMat) => {
    const ref = (refMat || "").trim();
    if (!ref) return;

    setPrecosRefMat(ref);
    setPrecos(null);
    setPrecosOpen(true);
    setPrecosLoading(true);

    const [dados, err] = await handleApi(
      api.post("Materiais/SelMaterialPrecos", { refmaterial: ref })
    );

    setPrecosLoading(false);

    if (err) {
      showAlert(err, "error");
      setPrecos(null);
      return;
    }

    console.log("dados", dados);

    setPrecos(dados); // objeto único vindo da API
  };

  const handleChangeMax = (e) => {
    const v = e.target.value;
    // permitimos vazio para o utilizador apagar e escrever de novo
    if (v === "") {
      setMaxFaturas("");
      return;
    }
    // apenas dígitos
    if (!/^\d+$/.test(v)) return;
    setMaxFaturas(v);
  };

  const itemContactos = entidade
    ? {
        cliente: entidade.nome || "",
        pagador: resumoContactos?.pagador_nome || "",
        vendedor: entidade.vendedor || "",
        refcliente: resumoContactos?.refcliente || entidade.ref || entidade.refentidade || "",
        refpagador: resumoContactos?.refpagador || "",
        refvendedor: resumoContactos?.refvendedor || refVendedorAtual || "",
      }
    : null;

  // -------------------- Dados do gráfico (últimos 3 anos, por mês) --------------------

  const anoAtual = new Date().getFullYear();
  const anosVendas = [anoAtual, anoAtual - 1, anoAtual - 2];

  const vendasPorAno = {};
  anosVendas.forEach((ano) => {
    vendasPorAno[ano] = new Array(12).fill(0);
  });

  (vendasResumo || []).forEach((row) => {
    const ano = Number(row.ano);
    const mes = Number(row.mes); // 1-12
    const qtd = Number(row.quantidade ?? row.qtd ?? 0);

    const mesIdx = mes - 1;
    if (!Number.isFinite(qtd)) return;
    if (!vendasPorAno[ano]) return;
    if (mesIdx < 0 || mesIdx > 11) return;

    vendasPorAno[ano][mesIdx] += qtd;
  });

  // arredondar e remover meses futuros do ano corrente
  Object.keys(vendasPorAno).forEach((anoStr) => {
    const ano = Number(anoStr);
    vendasPorAno[ano] = (vendasPorAno[ano] || []).map((v) => {
      if (v === null || typeof v !== "number" || Number.isNaN(v)) return v;
      return Math.round(v);
    });
  });

  const mesAtual = new Date().getMonth(); // 0-11
  for (let m = mesAtual + 1; m < 12; m += 1) {
    if (vendasPorAno[anoAtual]) {
      vendasPorAno[anoAtual][m] = null;
    }
  }

  const vendasDatasets = anosVendas.map((ano, idx) => ({
    label: String(ano),
    data: vendasPorAno[ano],
    color:
      idx === 0
        ? "warning" // ano corrente
        : idx === 1
        ? "primary" // ano anterior
        : "secondary", // antepenúltimo
  }));

  const vendasDatasetsFiltrados = vendasDatasets.filter((ds) =>
    (ds.data || []).some((v) => Number.isFinite(v) && v !== 0)
  );

  const vendasTotaisAno = vendasDatasetsFiltrados.reduce((acc, ds) => {
    const total = (ds.data || []).reduce((s, v) => (Number.isFinite(v) ? s + v : s), 0);
    acc[ds.label] = total;
    return acc;
  }, {});

  const temVendasResumo = vendasDatasetsFiltrados.length > 0;

  const vendasChart = {
    labels: mesesLabels,
    datasets: vendasDatasetsFiltrados.map((ds) => ({
      label: ds.label,
      data: ds.data,
      color: ds.color,
    })),
  };

  const propagacaoSubmarcasOrdenadas = useMemo(() => {
    return Array.isArray(propagacaoSubmarcas?.submarcas) ? propagacaoSubmarcas.submarcas : [];
  }, [propagacaoSubmarcas]);

  const propagacaoLinha = useMemo(() => {
    return propagacaoSubmarcas?.linha && typeof propagacaoSubmarcas.linha === "object"
      ? propagacaoSubmarcas.linha
      : null;
  }, [propagacaoSubmarcas]);

  const handleChangeFabricantePropagacao = async (_, novoFabricante) => {
    const refentidade = (entidade?.ref || entidade?.refentidade || "").trim();
    const reffabricante = (novoFabricante?.ref || "").toString().trim();

    setFabricantePropagacaoSel(novoFabricante || null);
    setPropagacaoSubmarcas(null);

    if (!refentidade || !reffabricante) {
      return;
    }

    const dataIniPropagacao = dataIniPropagacaoSel || "";
    const dataFimPropagacao = dataFimPropagacaoSel || "";

    setLoadingPropagacaoSubmarcas(true);

    const [dadosPropagacaoSubmarcas, errPropagacaoSubmarcas] = await handleApi(
      api.post("Comercial/SelPropagacaoSubmarcasCliente", {
        refcliente: refentidade,
        refvendedor: refVendedorAtual,
        reffabricante,
        dataini: dataIniPropagacao,
        datafim: dataFimPropagacao,
      })
    );

    setLoadingPropagacaoSubmarcas(false);

    if (errPropagacaoSubmarcas) {
      showAlert(errPropagacaoSubmarcas, "error");
      setPropagacaoSubmarcas(null);
      return;
    }

    const row =
      dadosPropagacaoSubmarcas && typeof dadosPropagacaoSubmarcas === "object"
        ? dadosPropagacaoSubmarcas
        : dadosPropagacaoSubmarcas?.data && typeof dadosPropagacaoSubmarcas.data === "object"
        ? dadosPropagacaoSubmarcas.data
        : null;

    setPropagacaoSubmarcas(row);
  };

  const handleAplicarPeriodoPropagacao = () => {
    if (!dataIniPropagacaoEdit || !dataFimPropagacaoEdit) {
      showAlert("Indica a data inicial e a data final.", "warning");
      return;
    }

    if (dataFimPropagacaoEdit < dataIniPropagacaoEdit) {
      showAlert("A data final não pode ser inferior à data inicial.", "warning");
      return;
    }

    setDataIniPropagacaoSel(dataIniPropagacaoEdit);
    setDataFimPropagacaoSel(dataFimPropagacaoEdit);
  };

  // -------------------- react-table (dados + colunas) --------------------

  const data = useMemo(() => {
    return (linhas || []).map((row) => {
      const qtd = row.qtd ?? row.quantidade;
      const qtdStr = formatNum(qtd, 2);
      const unidade = row.qtdunid || "";

      return {
        data: formatData(row.faturadata),
        faturanumero: row.faturanumero ?? "",
        item: row.item ?? "",
        itemTipo: row.catitemnome ?? "",
        refMat: row.refmaterial ?? "",
        nomeMat: row.nomematerial ?? "",
        qtdUnid: `${qtdStr}${unidade ? ` (${unidade})` : ""}`,
        precoAcons: formatNum(row.precoaconselhado),
        descCom: joinValPerc(row.desccomvalor, row.desccomperc),
        descProm: joinValPerc(row.descpromvalor, row.descpromperc),
        precoLiq: formatNum(row.precoliquido),
        iva: joinValPerc(row.ivavalor, row.ivaperc),
        precoTotal: formatNum(row.precototal),
        fabricante: row.fabricante ?? "",
      };
    });
  }, [linhas]);

  const columns = useMemo(() => {
    const base = [
      {
        Header: "",
        id: "acoes",
        disableFilters: true,
        width: 110,
        Cell: ({ row }) => (
          <MDButton
            color="warning"
            onClick={() => abrirPrecosMaterial(row.original.refMat)}
            sx={{ flex: "0 0 auto" }}
          >
            VER PREÇOS
          </MDButton>
        ),
      },

      {
        Header: "Data",
        accessor: "data",
      },
      {
        Header: "Nº Fatura",
        accessor: "faturanumero",
      },
      {
        Header: "Item Nº",
        accessor: "item",
        align: "right",
      },
      {
        Header: "Item Tipo",
        accessor: "itemTipo",
      },
      {
        Header: "Refª Mat.",
        accessor: "refMat",
      },
      {
        Header: "Nome do material",
        accessor: "nomeMat",
      },
      {
        Header: "Qtd. (Unid.)",
        accessor: "qtdUnid",
        align: "right",
      },
    ];

    if (podeVerValores) {
      base.push(
        {
          Header: () => (
            <Tooltip title="Preço Aconselhado" arrow>
              <span>Preço Acons.</span>
            </Tooltip>
          ),
          accessor: "precoAcons",
          align: "right",
        },
        {
          Header: () => (
            <Tooltip title="Desconto Comercial" arrow>
              <span>Desc. Com.</span>
            </Tooltip>
          ),
          accessor: "descCom",
          align: "right",
        },
        {
          Header: () => (
            <Tooltip title="Desconto Promocional" arrow>
              <span>Desc. Prom.</span>
            </Tooltip>
          ),
          accessor: "descProm",
          align: "right",
        },
        {
          Header: "Preço Líq.",
          accessor: "precoLiq",
          align: "right",
        },
        {
          Header: "IVA",
          accessor: "iva",
          align: "right",
        },
        {
          Header: "Preço Total",
          accessor: "precoTotal",
          align: "right",
        }
      );
    }

    base.push({
      Header: "Fabricante",
      accessor: "fabricante",
    });

    return base;
  }, [podeVerValores, podeVerPrecos]);

  const defaultColumn = useMemo(
    () => ({
      Filter: DefaultColumnFilter,
    }),
    []
  );

  const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow } = useTable(
    {
      columns,
      data,
      defaultColumn,
    },
    useFilters
  );

  const { margemTexto } = calcularMargem(precos);

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="xl" fullWidth>
        <DialogTitle>
          <div
            style={{
              display: "flex",
              alignItems: "flex-start",
              justifyContent: "space-between",
              gap: 12,
            }}
          >
            <div>{titulo}</div>

            <Tooltip title="Gerir contactos">
              <span>
                <IconButton
                  color="info"
                  onClick={() => setOpenContactos(true)}
                  disabled={!itemContactos?.refcliente}
                >
                  <Icon>calendar_month</Icon>
                </IconButton>
              </span>
            </Tooltip>
          </div>
        </DialogTitle>

        <DialogContent dividers>
          {/* ================== GRÁFICO ================== */}
          <h6 style={{ marginTop: 0, marginBottom: 8, fontSize: 13, color: "#7b809a" }}>
            Vendas da entidade (quantidades, nos últimos 3 anos)
          </h6>

          {loadingResumo ? (
            <div
              style={{
                padding: "16px 0",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <CircularProgress size={24} />
            </div>
          ) : !temVendasResumo ? (
            <div style={{ fontSize: 13, color: "#555", marginBottom: 16 }}>
              Não há vendas registadas para esta entidade nos últimos 3 anos
              {temFuncao("Comercial") && refVendedorAtual ? " com o seu código de vendedor." : "."}
            </div>
          ) : (
            <div style={{ marginBottom: 20 }}>
              {/* legenda das cores/anos */}
              <div
                style={{
                  fontSize: 12,
                  color: "#7b809a",
                  marginBottom: 8,
                  display: "flex",
                  flexWrap: "wrap",
                  gap: 20,
                }}
              >
                {vendasDatasetsFiltrados.map((ds) => (
                  <span key={ds.label} style={{ display: "inline-flex", alignItems: "center" }}>
                    <span
                      style={{
                        width: 10,
                        height: 10,
                        borderRadius: "50%",
                        marginRight: 6,
                        backgroundColor: colorMap[ds.color] || "#999",
                      }}
                    />
                    {ds.label}{" "}
                    <span style={{ color: "#3a3a3a", marginLeft: 4 }}>
                      (Qtd. total:
                      {Number(vendasTotaisAno[ds.label]).toLocaleString("pt-PT", {
                        minimumFractionDigits: 0,
                        maximumFractionDigits: 0,
                      })}
                      )
                    </span>
                  </span>
                ))}
              </div>

              <DefaultLineChart title="" description="" chart={vendasChart} />
            </div>
          )}

          {loadingPropagacaoSubmarcas || loadingFabricantesPropagacao ? (
            <div
              style={{
                padding: "6px 0 14px 0",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <CircularProgress size={20} />
            </div>
          ) : propagacaoSubmarcasOrdenadas.length > 0 ? (
            <div
              style={{
                marginBottom: 16,
                padding: "10px 12px",
                border: "1px solid #eee",
                borderRadius: 8,
                backgroundColor: "#fafafa",
                overflowX: "auto",
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                  flexWrap: "wrap",
                  marginBottom: 10,
                  fontSize: 12,
                  color: "#344767",
                  fontWeight: 600,
                }}
              >
                <span>Propagação nas submarcas do</span>

                {fabricantesPropagacao.length > 0 ? (
                  <div style={{ width: 300 }}>
                    <Autocomplete
                      size="small"
                      options={fabricantesPropagacao}
                      getOptionLabel={(option) => option?.nome || ""}
                      value={fabricantePropagacaoSel}
                      onChange={handleChangeFabricantePropagacao}
                      isOptionEqualToValue={(option, value) =>
                        (option?.ref || "").toString() === (value?.ref || "").toString()
                      }
                      renderInput={(params) => (
                        <TextField {...params} placeholder="Selecione fabricante…" />
                      )}
                      disableClearable
                    />
                  </div>
                ) : (
                  <span>{fabricantePropagacaoSel?.nome || "fabricante selecionado"}</span>
                )}

                <span>de</span>

                <div style={{ width: 150 }}>
                  <TextField
                    type="date"
                    size="small"
                    fullWidth
                    value={dataIniPropagacaoEdit}
                    onChange={(e) => setDataIniPropagacaoEdit(e.target.value)}
                  />
                </div>

                <span>a</span>

                <div style={{ width: 150 }}>
                  <TextField
                    type="date"
                    size="small"
                    fullWidth
                    value={dataFimPropagacaoEdit}
                    onChange={(e) => setDataFimPropagacaoEdit(e.target.value)}
                  />
                </div>

                <MDButton color="info" size="small" onClick={handleAplicarPeriodoPropagacao}>
                  Atualizar
                </MDButton>
              </div>

              <div style={{ minWidth: "max-content" }}>
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: `repeat(${propagacaoSubmarcasOrdenadas.length}, minmax(110px, 110px))`,
                    gap: 8,
                    marginBottom: 6,
                  }}
                >
                  {propagacaoSubmarcasOrdenadas.map((sm) => {
                    const ref = (sm?.ref || "").toString().trim();
                    const nome = (sm?.nome || ref || "").toString();

                    return (
                      <div
                        key={`titulo_${ref}`}
                        style={{
                          fontSize: 12,
                          color: "#344767",
                          fontWeight: 600,
                          textAlign: "center",
                          lineHeight: 1.2,
                          whiteSpace: "normal",
                          wordBreak: "break-word",
                        }}
                      >
                        {nome}
                      </div>
                    );
                  })}
                </div>

                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: `repeat(${propagacaoSubmarcasOrdenadas.length}, minmax(110px, 110px))`,
                    gap: 8,
                  }}
                >
                  {propagacaoSubmarcasOrdenadas.map((sm) => {
                    const ref = (sm?.ref || "").toString().trim();
                    const tem = Number(propagacaoLinha?.[`sm_${ref}`] || 0) === 1;

                    return (
                      <div
                        key={`valor_${ref}`}
                        style={{
                          textAlign: "center",
                          fontSize: 20,
                          lineHeight: 1,
                          color: "#7b809a",
                        }}
                      >
                        {tem ? "✔" : "—"}
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          ) : null}

          {loadingDebitosCliente ? (
            <div
              style={{
                padding: "6px 0 14px 0",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <CircularProgress size={20} />
            </div>
          ) : debitosCliente.length > 0 ? (
            <div
              style={{
                marginBottom: 16,
                padding: "10px 12px",
                border: "1px solid #eee",
                borderRadius: 8,
                backgroundColor: "#fafafa",
                overflowX: "auto",
              }}
            >
              <div
                style={{
                  fontSize: 12,
                  color: "#7b809a",
                  marginBottom: 10,
                  fontWeight: 600,
                }}
              >
                Débitos do cliente
              </div>

              <table
                style={{
                  width: "100%",
                  borderCollapse: "collapse",
                  fontSize: 12,
                  minWidth: 1100,
                }}
              >
                <thead>
                  <tr>
                    <th style={{ ...thStyle, color: "#344767" }}>Cliente</th>
                    <th style={{ ...thStyle, color: "#344767" }}>Pagador</th>
                    <th style={{ ...thStyle, color: "#344767" }}>Cond. Pagamento</th>
                    <th style={{ ...thStyle, textAlign: "right", color: "#344767" }}>
                      Saldo Vencido
                    </th>
                    <th style={{ ...thStyle, textAlign: "right", color: "#344767" }}>A Vencer</th>
                    <th style={{ ...thStyle, textAlign: "right", color: "#344767" }}>
                      Saldo Total
                    </th>
                    <th style={{ ...thStyle, textAlign: "right", color: "#344767" }}>
                      1 a 30 dias
                    </th>
                    <th style={{ ...thStyle, textAlign: "right", color: "#344767" }}>
                      31 a 60 dias
                    </th>
                    <th style={{ ...thStyle, textAlign: "right", color: "#344767" }}>
                      61 a 90 dias
                    </th>
                    <th style={{ ...thStyle, textAlign: "right", color: "#344767" }}>
                      91 a 120 dias
                    </th>
                    <th style={{ ...thStyle, textAlign: "right", color: "#344767" }}>
                      121 a 150 dias
                    </th>
                    <th style={{ ...thStyle, textAlign: "right", color: "#344767" }}>
                      Mais de 150 dias
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {debitosCliente.map((row, idx) => (
                    <tr key={idx}>
                      <td style={tdStyle}>
                        {(row?.refcliente || "") + (row?.cliente || "" ? ` - ${row.cliente}` : "")}
                      </td>
                      <td style={tdStyle}>
                        {(row?.refpagador || "") + (row?.pagador || "" ? ` - ${row.pagador}` : "")}
                      </td>
                      <td style={tdStyle}>{row?.condicoespagamento || "-"}</td>
                      <td style={{ ...tdStyle, textAlign: "right" }}>
                        {formatNum(row?.vencidototal)}
                      </td>
                      <td style={{ ...tdStyle, textAlign: "right" }}>{formatNum(row?.avencer)}</td>
                      <td style={{ ...tdStyle, textAlign: "right", fontWeight: 700 }}>
                        {formatNum(row?.saldototal)}
                      </td>
                      <td style={{ ...tdStyle, textAlign: "right" }}>
                        {formatNum(row?.dias_1a30)}
                      </td>
                      <td style={{ ...tdStyle, textAlign: "right" }}>
                        {formatNum(row?.dias_31a60)}
                      </td>
                      <td style={{ ...tdStyle, textAlign: "right" }}>
                        {formatNum(row?.dias_61a90)}
                      </td>
                      <td style={{ ...tdStyle, textAlign: "right", fontWeight: 700 }}>
                        {formatNum(row?.dias_91a120)}
                      </td>
                      <td style={{ ...tdStyle, textAlign: "right", fontWeight: 700 }}>
                        {formatNum(row?.dias_121a150)}
                      </td>
                      <td
                        style={{
                          ...tdStyle,
                          textAlign: "right",
                          fontWeight: 700,
                          color: Number(row?.dias_151mais || 0) > 0 ? "#d32f2f" : undefined,
                        }}
                      >
                        {formatNum(row?.dias_151mais)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : null}

          {/* ================== RESUMO DE CONTACTOS ================== */}
          {loadingContactosResumo ? (
            <div
              style={{
                padding: "8px 0 14px 0",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <CircularProgress size={20} />
            </div>
          ) : (
            <div style={{ marginBottom: 16 }}>
              <div style={{ fontSize: 13, lineHeight: 1.45 }}>
                <div>
                  <strong>Pagador:</strong>{" "}
                  {resumoContactos?.refpagador
                    ? `${resumoContactos.refpagador}${
                        resumoContactos?.pagador_nome ? ` - ${resumoContactos.pagador_nome}` : ""
                      }`
                    : "-"}
                </div>

                <div>
                  <strong>Último contacto:</strong>{" "}
                  {resumoContactos?.data_ultimo_contacto
                    ? formatDateTime(resumoContactos.data_ultimo_contacto)
                    : "-"}
                </div>

                <div
                  style={{
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    maxWidth: "100%",
                  }}
                  title={resumoContactos?.obs_ultimo_contacto || ""}
                >
                  <strong>Obs.:</strong> {resumoContactos?.obs_ultimo_contacto || "-"}
                </div>

                <div>
                  <strong>Próx. agendamento:</strong>{" "}
                  {resumoContactos?.data_proximo_agendamento
                    ? formatDateTime(resumoContactos.data_proximo_agendamento)
                    : "-"}
                </div>
              </div>
            </div>
          )}

          {/* Linha com nº de faturas */}
          <div
            style={{
              marginBottom: 8,
              fontSize: 12,
              color: "#7b809a",
              display: "flex",
              alignItems: "center",
              gap: 12,
              flexWrap: "wrap",
            }}
          >
            <span>
              Apresentadas as últimas
              <span style={{ marginLeft: 5, marginRight: 5 }}>
                <input
                  type="text"
                  value={maxFaturas}
                  onChange={handleChangeMax}
                  style={{
                    width: 40,
                    padding: "6px 6px",
                    fontSize: 12,
                    borderRadius: 4,
                    border: "1px solid #ccc",
                    textAlign: "center",
                  }}
                />
              </span>
              faturas de vendas (todas as linhas, ordenadas por data desc.).
            </span>
          </div>

          {/* separador visual entre gráfico e tabela */}
          <div
            style={{
              height: 1,
              backgroundColor: "#eee",
              marginBottom: 8,
            }}
          />

          {/* ================== TABELA ================== */}
          {loading ? (
            <div
              style={{
                padding: "24px 0",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <CircularProgress size={26} />
            </div>
          ) : linhas.length === 0 ? (
            <div style={{ fontSize: 13, color: "#555" }}>
              Não foram encontradas faturas de vendas para esta entidade
              {temFuncao("Comercial") && refVendedorAtual ? " com o seu código de vendedor." : "."}
            </div>
          ) : (
            <div
              style={{
                maxHeight: 420,
                overflowY: "auto",
                overflowX: "auto",
              }}
            >
              <table
                {...getTableProps()}
                style={{
                  width: "100%",
                  borderCollapse: "collapse",
                  fontSize: 13,
                  minWidth: 900,
                }}
              >
                <thead>
                  {headerGroups.map((headerGroup) => (
                    <tr key={headerGroup.id} {...headerGroup.getHeaderGroupProps()}>
                      {headerGroup.headers.map((column) => (
                        <th
                          key={column.id}
                          {...column.getHeaderProps()}
                          style={{
                            ...thStyle,
                            textAlign: column.align || "left",
                            position: "sticky",
                            top: 0,
                            zIndex: 2,
                            backgroundColor: "#f8f9fa",
                          }}
                        >
                          <div>{column.render("Header")}</div>
                          {column.canFilter && (
                            <div style={{ marginTop: 4 }}>{column.render("Filter")}</div>
                          )}
                        </th>
                      ))}
                    </tr>
                  ))}
                </thead>
                <tbody {...getTableBodyProps()}>
                  {rows.map((row) => {
                    prepareRow(row);
                    return (
                      <tr key={row.id} {...row.getRowProps()}>
                        {row.cells.map((cell) => (
                          <td
                            key={cell.column.id}
                            {...cell.getCellProps()}
                            style={{
                              ...tdStyle,
                              textAlign: cell.column.align || "left",
                            }}
                          >
                            {cell.render("Cell")}
                          </td>
                        ))}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </DialogContent>

        <DialogActions>
          <MDButton color="info" variant="gradient" size="small" onClick={onClose}>
            Fechar
          </MDButton>
        </DialogActions>
      </Dialog>

      {/* Dialog de preços por material (apenas se tiver permissão) */}
      <Dialog open={precosOpen} onClose={() => setPrecosOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Preços do material {precosRefMat ? `(${precosRefMat})` : ""}</DialogTitle>

        <DialogContent dividers>
          {precosLoading ? (
            <div style={{ padding: "24px 0", textAlign: "center" }}>
              <CircularProgress size={26} />
            </div>
          ) : !precos ? (
            <div style={{ fontSize: 13, color: "#555" }}>
              Não foram encontrados preços registados para este material.
            </div>
          ) : (
            <table
              style={{
                width: "100%",
                borderCollapse: "collapse",
                fontSize: 13,
                minWidth: 400,
              }}
            >
              <thead>
                <tr>
                  <th style={thStyle}>PV Acons.</th>
                  {podeVerPrecos && <th style={thStyle}>Margem</th>}
                  {podeVerPrecos && <th style={thStyle}>PC</th>}
                </tr>
              </thead>

              <tbody>
                <tr>
                  {/* PV aconselhado (zpr0) */}
                  <td style={tdStyle}>{formatNum(precos.zpr0)}</td>

                  {/* Margem em € e % (calculada a partir de zpr0 e zpvr) */}
                  {podeVerPrecos && <td style={tdStyle}>{margemTexto}</td>}

                  {/* Preço de custo (zpvr, já calculado dinamicamente na API para certos fabricantes) */}
                  {podeVerPrecos && <td style={tdStyle}>{formatNum(precos.zpvr)}</td>}
                </tr>
              </tbody>
            </table>
          )}
        </DialogContent>

        <DialogActions>
          <MDButton
            color="info"
            variant="gradient"
            size="small"
            onClick={() => setPrecosOpen(false)}
          >
            Fechar
          </MDButton>
        </DialogActions>
      </Dialog>

      <DialogClienteContactos
        open={openContactos}
        onClose={async () => {
          setOpenContactos(false);

          const refentidade = (entidade?.ref || entidade?.refentidade || "").trim();
          if (!refentidade) return;

          setLoadingContactosResumo(true);

          const [dadosResumoContactos, errResumoContactos] = await handleApi(
            api.post("Comercial/SelResumoContactos", {
              refcliente: refentidade,
              refpagador: refentidade,
              refvendedor: refVendedorAtual,
            })
          );

          setLoadingContactosResumo(false);

          if (errResumoContactos) {
            showAlert(errResumoContactos, "error");
            return;
          }

          const row =
            dadosResumoContactos && typeof dadosResumoContactos === "object"
              ? dadosResumoContactos
              : dadosResumoContactos?.data && typeof dadosResumoContactos.data === "object"
              ? dadosResumoContactos.data
              : null;

          setResumoContactos(row);
        }}
        item={itemContactos}
      />
    </>
  );
}

// estilos reutilizáveis
const thStyle = {
  textAlign: "left",
  padding: "6px 8px",
  fontSize: 12,
  color: "#7b809a",
  borderBottom: "1px solid #eee",
  whiteSpace: "nowrap",
};

const tdStyle = {
  padding: "8px 10px",
  borderTop: "1px solid #eee",
  fontSize: 13,
};

// filtro padrão (mesmo critério do Playbook)
function DefaultColumnFilter({ column: { filterValue, setFilter } }) {
  return (
    <input
      value={filterValue || ""}
      onChange={(e) => setFilter(e.target.value || undefined)}
      style={{
        width: "100%",
        fontSize: 11,
        padding: "2px 4px",
        borderRadius: 4,
        border: "1px solid #ddd",
      }}
      placeholder="filtrar…"
    />
  );
}
